/* Combined Listing picker. Child pages keep only their own product-linked values. */
if (!customElements.get('atl-combined-listing-picker')) {
  customElements.define('atl-combined-listing-picker', class extends HTMLElement {
    constructor() {
      super();
      this.addEventListener('change', (event) => this.loadSelection(event));
    }

    connectedCallback() {
      // Exactly one controller owns this section, including requests whose
      // responses replace the picker itself.
      this.section = this.closest('[id^="MainProduct-"]');
      if (!this.section) return;
      this.section.atlListingController = this;
      const url = new URL(window.location.href);
      if (this.dataset.childPage !== 'true' && !this.section.dataset.atlDefaultLoaded && !url.searchParams.has('variant') && !url.searchParams.has('option_values')) {
        // Pin the first parent-owned option values. Child-owned values are
        // omitted so Shopify can choose their defaults in that child.
        const defaults = [];
        this.querySelectorAll('fieldset, select').forEach((group) => {
          const first = group.querySelector('[data-option-value-id]');
          if (first?.dataset.productUrl) defaults.push(first.dataset.optionValueId);
        });
        if (defaults.length) this.loadSelection(null, defaults, false);
      }
    }

    disconnectedCallback() {
      this.controller?.abort();
    }

    async loadSelection(event, initialIds, updateHistory = true) {
      const changed = event?.target.matches('select')
        ? event.target.selectedOptions[0] : event?.target;
      if (event && !changed?.dataset.optionValueId) return;

      const selected = Array.from(this.querySelectorAll(
        'input[type="radio"]:checked, select option:checked'
      ));
      // On a parent-owned change, discard old child-specific selections.
      // IDs for the previous child's lengths/sizes need not exist in the next.
      const ids = initialIds || selected
        .filter((value) => !changed?.dataset.productUrl || value.dataset.productUrl)
        .map((value) => value.dataset.optionValueId);
      if (!ids.length || ids.some((id) => !id)) return;

      this.controller?.abort();
      const controller = this.controller = new AbortController();
      const section = this.section;
      const url = new URL(window.location.href);
      // Preserve locale, theme preview parameters, and advertising parameters.
      url.pathname = new URL(this.dataset.url, window.location.origin).pathname;
      url.searchParams.delete('variant');
      url.searchParams.delete('section_id');
      url.searchParams.delete('sections');
      url.searchParams.set('option_values', ids.join(','));
      const pageUrl = new URL(url);
      url.searchParams.set('section_id', this.dataset.section);
      const focusId = event?.target.id;
      const quantity = section.querySelector('input[name="quantity"]')?.value;
      const error = this.querySelector('[data-listing-error]');
      if (error) error.hidden = true;
      section.setAttribute('aria-busy', 'true');
      // Keep the picker responsive while preventing a stale SKU purchase.
      section.querySelectorAll('[name="add"], .shopify-payment-button').forEach((node) => {
        node.setAttribute('aria-disabled', 'true');
        if (node.matches('[name="add"]')) node.disabled = true;
        else node.style.pointerEvents = 'none';
      });
      section.querySelectorAll('input[name="id"]').forEach((node) => { node.disabled = true; });

      try {
        const response = await fetch(url, { signal: controller.signal });
        if (!response.ok) throw new Error(`Section request failed (${response.status})`);
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const source = html.getElementById(section.id);
        if (!source?.querySelector('atl-combined-listing-picker')) {
          throw new Error('Combined Listing section missing from response');
        }
        if (controller.signal.aborted || !this.isConnected) return;

        window.pauseAllMedia?.();
        // Dawn 5 moves all modal-dialog elements to document.body on connect.
        // Remove the previous section's moved modals before inserting new ones.
        const ownedIds = (section.dataset.atlModalIds || '').split(',').filter(Boolean);
        ownedIds.forEach((id) => {
          const modal = document.getElementById(id);
          if (!modal) return;
          if (modal.hasAttribute('open')) modal.hide?.();
          modal.remove();
        });

        // Insert an inert DOMParser node directly; connection upgrades Dawn's
        // custom elements after their full child markup is present.
        source.dataset.atlDefaultLoaded = 'true';
        section.replaceWith(source);
        const nextQuantity = source.querySelector('input[name="quantity"]');
        if (nextQuantity && quantity != null) nextQuantity.value = quantity;
        source.removeAttribute('aria-busy');
        if (updateHistory || initialIds) {
          window.history.replaceState(window.history.state, '', pageUrl);
        }
        source.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
          summary.setAttribute('role', 'button');
          summary.setAttribute('aria-expanded', summary.parentElement.hasAttribute('open'));
          if (summary.nextElementSibling?.id) summary.setAttribute('aria-controls', summary.nextElementSibling.id);
          summary.addEventListener('click', () => summary.setAttribute('aria-expanded', !summary.parentElement.open));
          summary.parentElement.addEventListener('keyup', onKeyUpEscape);
        });
        const share = source.querySelector('share-button');
        share?.updateUrl?.(pageUrl.href);
        window.Shopify?.PaymentButton?.init?.();
        if (source.querySelector('product-model')) window.ProductModel?.loadShopifyXR?.();
        if (focusId) document.getElementById(focusId)?.focus({ preventScroll: true });
        // For external apps, attach only their documented reinitialization API
        // to this event. Do not indiscriminately re-execute section scripts.
        source.dispatchEvent(new CustomEvent('atl:combined-listing:updated', {
          bubbles: true,
          detail: {
            section: source,
            parentUrl: pageUrl.href,
            childUrl: source.dataset.atlChildUrl,
            variantId: source.dataset.atlVariantId
          }
        }));
      } catch (err) {
        if (err.name === 'AbortError') return;
        console.error('Combined Listing selection failed', err);
        if (error) {
          error.hidden = false;
          error.querySelector('[data-listing-retry]').href = pageUrl.href;
        }
        // Keep buying disabled after failure: the controls now describe a
        // selection that has not been confirmed by the server.
      } finally {
        if (this.controller === controller && section.isConnected) {
          section.removeAttribute('aria-busy');
        }
      }
    }
  });
}

// Block keyboard activation of a stale accelerated checkout as well as clicks.
document.addEventListener('click', (event) => {
  if (event.target.closest?.('[id^="MainProduct-"] .shopify-payment-button[aria-disabled="true"]')) {
    event.preventDefault();
    event.stopImmediatePropagation();
  }
}, true);
